// pages/tenantBilling/BillingParts.jsx
// The small pieces both billing screens draw (doc/5.10.2026.md): the tenant
// form's Payment tab and the Plan page (Tenants → Action → Plan).
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Percent, IndianRupee, X, Lock, MessageSquare, CalendarDays, CalendarRange } from 'lucide-react';

import { inr } from './billingFormat';

const radioCard = (active) => `flex-1 min-w-[140px] flex items-start gap-2.5 rounded-xl border px-3 py-2.5 text-left cursor-pointer transition-colors ${
    active ? 'border-[#4ABDBD] bg-[#4ABDBD]/5' : 'border-gray-200 hover:border-[#4ABDBD]/60'}`;

export function PeriodRadio({ value, onChange, testId = 'billing-period' }) {
    const { t } = useTranslation();
    const options = [
        { id: 'monthly', icon: CalendarDays, label: t('tenantBilling.monthly', 'Monthly'), hint: t('tenantBilling.monthly_hint', 'One month from the start day') },
        { id: 'yearly', icon: CalendarRange, label: t('tenantBilling.yearly', 'Yearly'), hint: t('tenantBilling.yearly_hint', 'One year from the start day') },
    ];
    return (
        <div className="flex flex-wrap gap-2" role="radiogroup" data-testid={testId}>
            {options.map((o) => {
                const Icon = o.icon;
                return (
                    <button key={o.id} type="button" role="radio" aria-checked={value === o.id}
                        data-testid={`${testId}-${o.id}`} onClick={() => onChange(o.id)} className={radioCard(value === o.id)}>
                        <Icon className={`w-4 h-4 mt-0.5 flex-none ${value === o.id ? 'text-[#2D8A8A]' : 'text-gray-400'}`} />
                        <span>
                            <span className="block text-[13px] font-semibold text-gray-700">{o.label}</span>
                            <span className="block text-[11px] text-gray-500">{o.hint}</span>
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

export function PlanEndActionRadio({ value, onChange, testId = 'billing-end-action' }) {
    const { t } = useTranslation();
    const options = [
        { id: 'lock', icon: Lock, label: t('tenantBilling.end_lock', 'Lock'), hint: t('tenantBilling.end_lock_hint', 'Nobody at this organisation (admin, doctors, staff) can sign in. Data is safe.') },
        { id: 'message', icon: MessageSquare, label: t('tenantBilling.end_message', 'Message only'), hint: t('tenantBilling.end_message_hint', 'The app keeps working, with a banner asking them to renew.') },
    ];
    return (
        <div className="flex flex-wrap gap-2" role="radiogroup" data-testid={testId}>
            {options.map((o) => {
                const Icon = o.icon;
                return (
                    <button key={o.id} type="button" role="radio" aria-checked={value === o.id}
                        data-testid={`${testId}-${o.id}`} onClick={() => onChange(o.id)} className={radioCard(value === o.id)}>
                        <Icon className={`w-4 h-4 mt-0.5 flex-none ${value === o.id ? 'text-[#2D8A8A]' : 'text-gray-400'}`} />
                        <span>
                            <span className="block text-[13px] font-semibold text-gray-700">{o.label}</span>
                            <span className="block text-[11px] text-gray-500">{o.hint}</span>
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

/**
 * Discount: none / percentage / flat amount, and the value. `cap` is the
 * largest value that makes sense (100 for a percentage, the features total
 * for an amount); an out-of-range value is pulled back on blur, never while
 * typing.
 */
export function DiscountControl({ discount, onChange, cap, testId = 'billing-discount' }) {
    const { t } = useTranslation();
    const setType = (type) => onChange({ type, value: discount.type === type ? discount.value : '' });
    const kinds = [
        { id: null, label: t('tenantBilling.no_discount', 'No discount') },
        { id: 'percentage', label: t('tenantBilling.discount_percentage', 'Percentage'), icon: Percent },
        { id: 'amount', label: t('tenantBilling.discount_amount', 'Flat amount'), icon: IndianRupee },
    ];
    return (
        <div className="flex flex-wrap items-center gap-2" data-testid={testId}>
            <div className="inline-flex rounded-xl border border-gray-200 p-0.5 bg-gray-50">
                {kinds.map((k) => {
                    const Icon = k.icon;
                    const active = (discount.type || null) === k.id;
                    return (
                        <button key={String(k.id)} type="button" data-testid={`${testId}-type-${k.id || 'none'}`}
                            onClick={() => setType(k.id)}
                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer ${
                                active ? 'bg-white text-[#2D8A8A] shadow-sm' : 'text-gray-500 hover:text-[#2D8A8A]'}`}>
                            {Icon && <Icon className="w-3.5 h-3.5" />}
                            {k.label}
                        </button>
                    );
                })}
            </div>
            {discount.type && (
                <span className="inline-flex items-center gap-1">
                    <input data-testid={`${testId}-value`} value={discount.value} inputMode="decimal"
                        placeholder={discount.type === 'percentage' ? '10' : '5000'}
                        onChange={(e) => onChange({ ...discount, value: e.target.value.replace(/[^\d.]/g, '') })}
                        onBlur={() => {
                            const n = Number(discount.value);
                            if (discount.value === '' || !Number.isFinite(cap) || cap < 0) return;
                            if (!Number.isFinite(n) || n < 0) onChange({ ...discount, value: '' });
                            else if (n > cap) onChange({ ...discount, value: String(cap) });
                        }}
                        className="w-24 px-2.5 py-1.5 text-[12px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]" />
                    <span className="text-[11px] text-gray-500">{discount.type === 'percentage' ? '%' : '₹'}</span>
                    <button type="button" data-testid={`${testId}-clear`} onClick={() => onChange({ type: null, value: '' })}
                        title={t('tenantBilling.discount_clear', 'Remove discount')}
                        className="p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer">
                        <X className="w-3.5 h-3.5" />
                    </button>
                </span>
            )}
        </div>
    );
}

/** The features with their prices and the three lines + total, as the invoice will show them. */
export function PlanLines({ lines, featuresTotal, discountAmount, creditAmount = 0, total, period, showPrices = true, testId = 'billing-lines' }) {
    const { t } = useTranslation();
    const per = period === 'yearly' ? t('tenantBilling.per_year', '/ year') : t('tenantBilling.per_month', '/ month');
    return (
        <div data-testid={testId} className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                {lines.length === 0 && (
                    <p className="px-3 py-3 text-[12px] text-gray-500">{t('tenantBilling.no_features', 'No features are ticked on the Services & Features tab.')}</p>
                )}
                {lines.map((l) => (
                    <div key={l.featureId} className="flex items-center justify-between gap-3 px-3 py-1.5 text-[12px]">
                        <span className="text-gray-700 min-w-0 truncate">{l.label}</span>
                        {showPrices && <span className="text-gray-500 tabular-nums flex-none">{inr(l.price)}</span>}
                    </div>
                ))}
            </div>
            <div className="bg-[#F7FBFA] px-3 py-2.5 space-y-1 text-[12px]">
                <div className="flex justify-between" data-testid={`${testId}-features-total`}>
                    <span className="text-gray-600">{t('tenantBilling.features_total', 'Features total')}</span>
                    <span className="tabular-nums text-gray-700">{inr(featuresTotal)}</span>
                </div>
                {discountAmount > 0 && (
                    <div className="flex justify-between" data-testid={`${testId}-discount`}>
                        <span className="text-gray-600">{t('tenantBilling.discount', 'Discount')}</span>
                        <span className="tabular-nums text-gray-700">− {inr(discountAmount)}</span>
                    </div>
                )}
                {creditAmount > 0 && (
                    <div className="flex justify-between" data-testid={`${testId}-credit`}>
                        <span className="text-gray-600">{t('tenantBilling.credit', 'Credit from previous plan')}</span>
                        <span className="tabular-nums text-gray-700">− {inr(creditAmount)}</span>
                    </div>
                )}
                <div className="flex justify-between items-baseline pt-1 border-t border-[#C7D6D5]" data-testid={`${testId}-total`}>
                    <span className="text-[11px] font-extrabold uppercase tracking-widest text-[#2D8A8A]">{t('tenantBilling.total', 'Total')}</span>
                    <span className="text-base font-extrabold text-[#2D8A8A] tabular-nums">{inr(total)} <span className="text-[11px] font-semibold">{per}</span></span>
                </div>
            </div>
        </div>
    );
}

export function InvoiceStatusChip({ status, testId }) {
    const { t } = useTranslation();
    const tone = {
        paid: 'bg-[#4ABDBD]/10 text-[#2D8A8A] border-[#4ABDBD]/40',
        unpaid: 'bg-[#D7A05C]/15 text-[#8a5a1a] border-[#D7A05C]/40',
        void: 'bg-gray-100 text-gray-500 border-gray-200',
    }[status] || 'bg-gray-100 text-gray-500 border-gray-200';
    const label = {
        paid: t('tenantBilling.paid', 'Paid'),
        unpaid: t('tenantBilling.unpaid', 'Unpaid'),
        void: t('tenantBilling.void', 'Cancelled'),
    }[status] || status;
    return <span data-testid={testId} className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${tone}`}>{label}</span>;
}

export function PlanPhaseChip({ phase, testId }) {
    const { t } = useTranslation();
    const tone = {
        current: 'bg-[#4ABDBD]/10 text-[#2D8A8A] border-[#4ABDBD]/40',
        scheduled: 'bg-[#9DB3B1]/15 text-[#4f6664] border-[#9DB3B1]/50',
        ended: 'bg-gray-100 text-gray-600 border-gray-200',
        stopped: 'bg-gray-100 text-gray-600 border-gray-200',
        cancelled: 'bg-gray-100 text-gray-400 border-gray-200',
    }[phase] || 'bg-gray-100 text-gray-600 border-gray-200';
    const label = {
        current: t('tenantBilling.phase_current', 'Current'),
        scheduled: t('tenantBilling.phase_scheduled', 'Starts later'),
        ended: t('tenantBilling.phase_ended', 'Ended'),
        stopped: t('tenantBilling.phase_stopped', 'Stopped'),
        cancelled: t('tenantBilling.phase_cancelled', 'Cancelled'),
    }[phase] || phase;
    return <span data-testid={testId} className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${tone}`}>{label}</span>;
}
